(ns pfo.automation.component
  (:require
   [pfo.controller :as controller :refer [subscribe]]
   [applied-science.js-interop :as j]
   [reagent.core :as reagent :refer [atom track!]]
   ["react-select/creatable" :default CreatableSelect]
   ["react-select/async" :default Async]
   ["react-select" :default Select :refer [createFilter components]]
   ["react-virtuoso" :refer [Virtuoso]]
   [pfo.label.component :as label]
   [pfo.util :as util]
   [promesa.core :as p]
   [pfo.widget.autocomplete :refer [react-select-styles]]))

(def Option (.-Option components))
(defn disposition-menu-list-option [props]
  [:> Option
   (update props :innerProps
     (fn [inner-props]
       (j/assoc! inner-props
         :onMouseOver nil
         :onMouseMove nil)
       inner-props))])

(defn disposition-menu-list [props]
  [:> Virtuoso
   {:style {:height "400px"}
    :data (:children props)
    :itemContent (fn [index elem] elem)}])

(defn dispositions-format [dispositions]
  (mapv
    (fn [disposition]
      {:label (:uid disposition)
       :label-id (:label-id disposition)
       :label-name (:label-name disposition)
       :value (:id disposition)
       :new (boolean (:new disposition))})
    dispositions))

(defn disposition-chooser-fuzzy [options value-atom]
  (let [allow-create-new (:allow-create-new options)
        dispositions-atom (or (:dispositions-atom options) (subscribe :automation-dispositions))
        disposition-list (track! #(:results @dispositions-atom))]
    (fn [options _]
      (let [dispositions (if-let [filter-pred (:filter-pred options)]
                           (filter filter-pred @disposition-list)
                           @disposition-list)]
        [:> (if (:async options) Async (if allow-create-new CreatableSelect Select))
         (merge
           {:placeholder (if (:disabled options) "" "Choose")
            :isDisabled  (or (not (seq options)) (:disabled options))
            :ref (fn [el] (some-> (:mount-cb options) (apply [el])))
            :defaultMenuIsOpen (boolean (:default-open options))

            :components {:MenuList (reagent/reactify-component disposition-menu-list)
                         :Option (reagent/reactify-component disposition-menu-list-option)}
            :filterOption (createFilter (clj->js {:ignoreAccents false}))
            :value       (filter
                           (fn [disposition] (= (:id @value-atom) (:value disposition)))
                           (dispositions-format dispositions))

            :on-change   (fn [e]
                           (let [ev (js->clj e :keywordize-keys true)
                                 is-new (:__isNew__ ev)
                                 value (:value ev)]
                             (if is-new
                               (do
                                 (reset! value-atom {:id value :name value :new true})
                                 (some-> (:create-cb options) (apply [{:name value :id value :new true}]))
                                 (swap! dispositions-atom update :results conj {:uid value :id value :new true}))
                               (do
                                 (reset! value-atom {:id value :new false})
                                 (some-> (:change-cb options) (apply [(merge {:id value :new (:new ev) :label (:label ev)}
                                                                        (when (:new ev)
                                                                          {:name value}))]))))))
            :on-blur (fn [_] (some-> (:blur-cb options) (apply [])) )}
           (:primary react-select-styles)
           (if (:async options)
             {:loadOptions (:load-options-fn options)}
             {:options (if (and (:allow-empty options) (not-empty (str (:id @value-atom))))
                         (merge (dispositions-format dispositions) {:label "Not selected" :value ""})
                         (dispositions-format dispositions))})
           (:react-select-options options))] ))))


(defn disposition-and-label-chooser [options family-id value-atom]
  (let [dispositions-atom (:dispositions-atom options)
        labels-atom (:labels-atom options)
        dispositions-format (fn [dispositions]
                              (mapv
                                (fn [disposition]
                                  {:label (str (:uid disposition))
                                   :value {:id (:id disposition)
                                           :label-name (:label-name disposition)
                                           :label-new (:label-new disposition)
                                           :label-id (:label-id disposition)}
                                   :new (boolean (:new disposition))})
                                dispositions))
        labels-format (fn [labels]
                        (mapv
                          (fn [label]
                            {:label (:name label)
                             :value (:id label)
                             :new (boolean (:new label))})
                          labels))]

    (fn [options family-id _]
      (let [dispositions (if-let [filter-pred (:filter-pred options)]
                           (filter filter-pred @dispositions-atom)
                           @dispositions-atom)
            dmap (util/associate-by :id @dispositions-atom)
            formatted-dispositions (dispositions-format dispositions)
            formatted-labels (labels-format @labels-atom)]
        [:div
         [:> CreatableSelect
          (merge
            {:options     (if (and (:allow-empty options) (not-empty (str (:id @value-atom))))
                            (merge formatted-dispositions {:label "Not selected" :value ""})
                            formatted-dispositions)
             :placeholder (if (:disabled options) "" "Choose")
             :isDisabled  (or (not (seq options)) (:disabled options))
             :value       (filter
                            (fn [disposition] (= (:id @value-atom) (:id (:value disposition))))
                            formatted-dispositions)

             :on-change   (fn [e]
                            (let [ev (js->clj e :keywordize-keys true)
                                  is-new (:__isNew__ ev)
                                  value (:value ev)]

                              (if is-new
                                (do
                                  ;; In "new" case, the value is only the string
                                  (reset! value-atom {:id value :name value :new true})
                                  (some-> (:change-cb options) (apply [{:name value :id value :new true}]))
                                  (swap! dispositions-atom conj {:uid value :id value :new true}))
                                (do
                                  (reset! value-atom {:id (:id value)
                                                      :label-id (:label-id value)
                                                      :new false})
                                  (some-> (:change-cb options) (apply [(merge {:id (:id value)
                                                                               :label-id (:label-id value)
                                                                               :new (:new ev)}
                                                                         (when (:new ev)
                                                                           {:name (:name value)}))]))))))
             :filterOption (createFilter (clj->js {:ignoreAccents false}))
             :components {:MenuList (reagent/reactify-component disposition-menu-list)
                          :Option (reagent/reactify-component disposition-menu-list-option)}
             :on-input-change (fn [text] (util/truncate (clojure.string/replace text #"[^\w-]" "") 254))}
            (:primary react-select-styles))]

         [:> CreatableSelect
          (merge
            {:options     (if (and (:allow-empty options) (not-empty (str (:id @value-atom))))
                            (merge formatted-labels {:label "Not selected" :value nil})
                            formatted-labels)
             :placeholder (if (:disabled options) "" "Choose")
             :isDisabled  (or (not (seq options)) (:disabled options))
             :value       (filter
                            (fn [label] (= (get-in dmap [(:id @value-atom) :label-id]) (:value label)))
                            formatted-labels)

             :on-change    (fn [e]
                             (let [ev (js->clj e :keywordize-keys true)
                                   is-new (:__isNew__ ev)
                                   value (:value ev)]

                               (if is-new
                                 (do
                                   (swap! value-atom assoc
                                     :label-id value
                                     :label-name value
                                     :label-new true)

                                   (swap! labels-atom conj {:name value :id value :new true})
                                   (some-> (:change-cb options) (apply [(merge @value-atom {:label-name value :label-id value :label-new true})])))
                                 (do
                                   (swap! value-atom assoc
                                     :label-id value
                                     :label-new false)
                                   (some->
                                     (:change-cb options)
                                     (apply [(merge @value-atom {:label-id value :label-new (:new ev)}
                                               (when (:new ev)
                                                 {:label-name value}))]))))))}
            (:primary react-select-styles))]] ))))

(defn chooser-dispositions-atom []
  (let [disposition-list-atom (subscribe :automation-dispositions)
        disposition-list-flat-atom (atom nil)
        setup-flat-atom! (fn [paginated-list]
                           (reset! disposition-list-flat-atom
                             (map (fn [ditem]
                                    {:id (:id ditem)
                                     :uid (:uid ditem)
                                     :label-id (get-in ditem [:labels 0 :id])})
                               (:results paginated-list))))]
    (when (seq @disposition-list-atom)
      (setup-flat-atom! @disposition-list-atom))
    (add-watch disposition-list-atom :flatten-disposition-list
      (fn [_ _ _ dlist]
        (setup-flat-atom! dlist)))
    disposition-list-flat-atom))

(defn update-disposition-choices! [dispositions-atom updated-value]
  (swap! dispositions-atom
    (fn [dlist]
      (map (fn [disposition]
             (if (= (:id disposition) (:id updated-value))
               (merge disposition updated-value)
               disposition)) dlist))))

(defn chooser-labels-atom []
  (let [label-list-atom (subscribe :label-list)
        label-list-flat-atom (atom nil)
        setup-flat-atom! (fn [paginated-list]
                           (reset! label-list-flat-atom
                             (map (fn [litem]
                                    {:id (:id litem)
                                     :name (:name litem)})
                               (:results paginated-list))))]
    (when (seq @label-list-atom)
      (setup-flat-atom! @label-list-atom))
    (add-watch label-list-atom :flatten-label-list
      (fn [_ _ _ dlist]
        (setup-flat-atom! dlist)))
    label-list-flat-atom))

(defn chooser-disposition-value-atoms [dispositions]
  (vec (or
         (seq (map
                (fn [disposition]
                  (atom {:new false
                         :id (:id disposition)
                         :label-id (get-in disposition [:labels 0 :id])}))
                dispositions))
         [(atom nil)])))

(defn disposition-name [disposition]
  [:span (:uid disposition) " "
   (when-let [labels (seq (:labels disposition))]
     [label/label (:name (first labels))])])
